import { TimelineItem, ItemFilters, ActiveFunctions, ItemCategory } from '@/types/item';

export interface ItemFilterOptions {
  // Basic filters
  filters: ItemFilters;
  activeFunctions: ActiveFunctions;
  
  // Project permissions (for ItemsManagement with multiple projects)
  selectedProject?: string;
  allowedProjectIds?: string[];
  
  // Category permissions
  canViewCategory: (category: ItemCategory) => boolean;
  
  // Debug logging
  enableDebugLog?: boolean;
  logPrefix?: string;
}

/**
 * Shared filtering logic for timeline items used across ItemsManagement and Weekly Plan
 * This ensures both components apply identical filtering rules
 */
export const filterItemsByPermissionsAndFilters = (
  items: TimelineItem[], 
  options: ItemFilterOptions
): TimelineItem[] => {
  // Defensive defaults to prevent TypeError
  const {
    filters = {
      search: '',
      activeGates: { g1: true, g2: true, g3: true, g4: true, g5: true }
    },
    activeFunctions = { shop: true, specs: true, po: true, contract: true },
    selectedProject,
    allowedProjectIds,
    canViewCategory,
    enableDebugLog = false,
    logPrefix = 'ItemsFilter'
  } = options || {};

  if (enableDebugLog) {
    console.debug(`[${logPrefix}] Starting filter - Raw items:`, items?.length || 0);
    console.debug(`[${logPrefix}] Filters:`, filters);
    console.debug(`[${logPrefix}] Active functions:`, activeFunctions);
    console.debug(`[${logPrefix}] Item IDs:`, items?.map(i => i.id) || []);
  }

  // Return empty array if no items provided
  if (!items || !Array.isArray(items)) {
    if (enableDebugLog) {
      console.debug(`[${logPrefix}] No items provided, returning empty array`);
    }
    return [];
  }

  const filteredItems = items.filter(item => {
    // Step 1: Project permission check
    if (selectedProject) {
      // If specific project selected, item must match that project
      if (item.projectId !== selectedProject) {
        return false;
      }
    } else if (allowedProjectIds) {
      // If "All projects" selected, item must be in allowed projects list
      if (!allowedProjectIds.includes(item.projectId)) {
        return false;
      }
    }

    // Step 2: Category permissions check - REMOVED
    // RLS governs visibility; UI-level category permissions should not hide items.
    
    // Step 3: Search filter
    if (filters.search && !item.name.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    
    // Step 4: Category filter (different from permissions - this is user selection)
    // Support both new multi-select (categories) and legacy single-select (category)
    if (filters.categories !== undefined) {
      // [] = không chọn danh mục nào → không item nào lọt
      if (!filters.categories.includes(item.category)) {
        return false;
      }
    } else if (filters.category && item.category !== filters.category) {
      // Backward compatible with old single value
      return false;
    }
    
    // Step 5: Gate filter - only apply if not all gates are active
    const allGatesActive = Object.values(filters.activeGates).every(active => active);
    if (!allGatesActive) {
      const activeFunctionsList = Object.entries(activeFunctions).filter(([_, active]) => active);
      if (activeFunctionsList.length > 0) {
        const hasActiveGateWithValue = activeFunctionsList.some(([fnName]) => 
          Object.entries(filters.activeGates).some(([gateName, gateActive]) => {
            if (!gateActive) return false;
            const functionData = item.timelineData[fnName as keyof typeof item.timelineData];
            if (functionData && typeof functionData === 'object' && gateName in functionData) {
              const gateData = functionData[gateName as keyof typeof functionData];
              return gateData && typeof gateData === 'object' && ('start' in gateData || 'end' in gateData);
            }
            return false;
          })
        );
        if (!hasActiveGateWithValue) return false;
      }
    }
    
    // Step 6: Status filter - ALWAYS apply regardless of project context
    if (filters.status && filters.status.length > 0) {
      const itemStatusLower = (item.projectStatus || '').toLowerCase();
      const selectedStatusesLower = filters.status.map(s => s.toLowerCase());
      
      if (enableDebugLog) {
        console.debug(`[${logPrefix}] Status filter check:`, {
          itemName: item.name,
          itemProjectStatus: item.projectStatus,
          itemStatusLower,
          selectedStatuses: filters.status,
          selectedStatusesLower
        });
      }
      
      const matches = itemStatusLower && selectedStatusesLower.includes(itemStatusLower);
      
      if (enableDebugLog && !matches) {
        console.debug(`[${logPrefix}] Item filtered out by status:`, {
          itemName: item.name,
          itemStatus: item.projectStatus,
          itemStatusLower,
          selectedStatuses: filters.status,
          reason: !itemStatusLower ? 'missing projectStatus' : 'status not in filter list'
        });
      }
      
      if (!matches) return false;
    }
    
    return true;
  });

  if (enableDebugLog) {
    console.debug(`[${logPrefix}] After permissions/filters:`, filteredItems.length);
    console.debug(`[${logPrefix}] Filtered item IDs:`, filteredItems.map(i => i.id));
  }

  return filteredItems;
};